import { EvalaZoomCode } from "@odata/GeneratedEnums";
import { isDefined } from "@utils/general";
import { cloneDeep } from "lodash";
import { CSSProperties } from "react";
import { DraggableProvidedDraggableProps, DraggingStyle } from "react-beautiful-dnd";

import { isFirefox, isSafari } from "../global.utils";

export const getZoomLevel = (): number => {
    const r = document.querySelector(":root") as HTMLElement;
    return parseFloat(window.getComputedStyle(r).getPropertyValue('--zoom'));
};

export const getZoomCorrectionLevel = (): number => {
    const r = document.querySelector(":root") as HTMLElement;
    return parseFloat(window.getComputedStyle(r).getPropertyValue('--zoom-correction'));
};

export const updateZoomLevel = (zoomCode: EvalaZoomCode): void => {
    let zoom: number;
    switch (zoomCode) {
        case EvalaZoomCode.L:
            zoom = 1;
            break;
        case EvalaZoomCode.M:
            zoom = 0.9;
            break;
        case EvalaZoomCode.S:
        default:
            zoom = 0.8;
            break;
    }
    setZoomLevel(zoom);
};

export const setZoomLevel = (zoom: number): void => {
    const r = document.querySelector(":root") as HTMLElement;

    if (isFirefox()) {
        // firefox is broken
        // appZoom = 1;
        // r.style.setProperty('--zoom', "1");
        // return;
    }

    if (isSafari()) {
        // safari already has correct vh and vw sizes, so there is no need to recalculate in js
        r.style.setProperty('--zoom-correction', "1");
    } else {
        r.style.setProperty('--zoom-correction', zoom.toString());
    }

    r.style.setProperty('--zoom', zoom.toString());
};

export const updateStyleAccordingToZoomLevel = (style: CSSProperties): CSSProperties => {
    const zoom = getZoomLevel();
    if (zoom === 1) {
        // No zoom applied, return the style as is
        return { ...style };
    }

    const updatedStyle = { ...style }; // Clone the style object
    const transform = style.transform;

    if (transform) {
        // Match the translate function in the transform property
        const translateMatch = transform.match(/translate\(([^)]+)\)/);
        if (translateMatch) {
            const translateValues = translateMatch[1].split(',').map(value => parseFloat(value.trim()));
            if (translateValues.length === 2 && !isNaN(translateValues[0]) && !isNaN(translateValues[1])) {
                const [x, y] = translateValues.map(value => value / zoom);
                // Replace only the translate part, preserving other transform properties
                updatedStyle.transform = transform.replace(
                        /translate\([^)]+\)/,
                        `translate(${x}px, ${y}px)`
                );
            }
        }
    }

    return cloneDeep(updatedStyle);
};

export function getRectNormalized(element: Element, useZoomCorrectionConstant = false): DOMRect {
    if (!element) {
        return null;
    }

    const rect = element.getBoundingClientRect();

    const zoom = useZoomCorrectionConstant ? getZoomCorrectionLevel() : getZoomLevel();

    return {
        ...rect,
        x: rect.x / zoom,
        y: rect.y / zoom,
        top: rect.top / zoom,
        left: rect.left / zoom,
        bottom: rect.bottom / zoom,
        right: rect.right / zoom,
        width: rect.width / zoom,
        height: rect.height / zoom
    };
}

export function normalizeProp(value: number): number {
    const zoom = getZoomLevel();
    return value / zoom;
}

export function getStyleFromDraggableProps(draggableProps: DraggableProvidedDraggableProps): CSSProperties {
    const providedStyle = draggableProps.style as DraggingStyle;
    return isDefined(providedStyle.top) ? {
        ...updateStyleAccordingToZoomLevel(providedStyle as CSSProperties),
        top: normalizeProp(providedStyle.top),
        left: normalizeProp(providedStyle.left),
        width: normalizeProp(providedStyle.width),
        height: normalizeProp(providedStyle.height),
    } : providedStyle;
}